import * as p from '@clack/prompts'
import {
  type AddFieldOption,
  type AddFieldPlacementTarget,
  applySlotToField,
  buildLeafField,
  buildPlacementTargets,
  deriveFieldNameFromLabel,
  ENTITY_SINGLE_ADD_FIELD_TYPES,
  FORM_ADD_FIELD_TYPES,
  FORM_OPTION_FIELD_TYPES,
  type GeneratedSchemaField,
  type GeneratedSchemaKind,
  type LoadedAuthoredGeneratedSchema,
  type RelationshipTargetKind,
  readRelationshipTargetKind,
  validateGeneratedFieldName
} from '@core-engine/schema/add-field.js'
import { listSchemaNames } from '@core-engine/schema/schema-reader.js'
import { USER_RELATIONSHIP_TARGET } from '@core-engine/schema/system-relationships.js'
import type {
  FormColumn,
  FormField,
  FormSchema,
  Schema,
  SchemaColumn,
  SchemaField
} from '@core-engine/schema/types.js'

export interface SchemaPromptContext {
  cancelMessage: string
}

export interface PromptTextOptions {
  defaultValue?: string
  placeholder?: string
  validate?: (input: string) => string | undefined
}

export interface PromptFieldOptions {
  deriveName?: boolean
  derivePlaceholder?: boolean
  existingNames?: Iterable<string>
  promptAdvancedOptions?: boolean
  promptHeight?: boolean
  promptHint?: boolean
  typeFirst?: boolean
}

export interface PreparedFieldInsertion {
  field: GeneratedSchemaField
  target: AddFieldPlacementTarget
  order: {
    before?: string
    after?: string
  }
  summary: string[]
}

export function isInteractiveSession(): boolean {
  return Boolean(process.stdin.isTTY) && process.env.CI !== 'true'
}

export function fail(message: string): never {
  p.log.error(message)
  process.exit(1)
}

export function cancel(context: SchemaPromptContext): never {
  p.cancel(context.cancelMessage)
  process.exit(0)
}

function cancelIfNeeded<T>(context: SchemaPromptContext, value: T | symbol): T {
  if (p.isCancel(value)) {
    cancel(context)
  }
  return value
}

export async function promptConfirm(
  context: SchemaPromptContext,
  message: string,
  initialValue = true
): Promise<boolean> {
  const confirmed = await p.confirm({ message, initialValue })
  if (p.isCancel(confirmed)) {
    cancel(context)
  }
  return Boolean(confirmed)
}

type FieldCheckboxOption = 'required' | 'multiple'

export async function promptText(
  context: SchemaPromptContext,
  message: string,
  optionsOrDefaultValue?: string | PromptTextOptions
): Promise<string> {
  const options =
    typeof optionsOrDefaultValue === 'string'
      ? { defaultValue: optionsOrDefaultValue }
      : (optionsOrDefaultValue ?? {})
  const value = await p.text({
    message,
    defaultValue: options.defaultValue,
    placeholder: options.placeholder,
    validate(input) {
      const result = options.validate?.(String(input ?? ''))
      if (result) {
        return result
      }
      if (!input || !String(input).trim()) {
        return 'Enter a value.'
      }
    }
  })

  return String(cancelIfNeeded(context, value)).trim()
}

export async function promptOptionalText(
  context: SchemaPromptContext,
  message: string
): Promise<string | undefined> {
  const value = await p.text({
    message,
    placeholder: 'Leave blank to skip'
  })

  const result = String(cancelIfNeeded(context, value)).trim()
  return result || undefined
}

export async function promptSelectValue<T extends string>(
  context: SchemaPromptContext,
  message: string,
  options: Array<{ value: T; label: string }>,
  initialValue?: T
): Promise<T> {
  const value = await p.select<T>({
    message,
    options: options as never,
    initialValue
  })
  return cancelIfNeeded(context, value) as T
}

export function formatKind(kind: RelationshipTargetKind): string {
  if (kind === 'user') return 'User'
  if (kind === 'form') return 'form'
  if (kind === 'single') return 'Single'
  return 'Entity'
}

export async function createInteractiveInsertion(
  context: SchemaPromptContext,
  loaded: LoadedAuthoredGeneratedSchema,
  schemasDir: string
): Promise<PreparedFieldInsertion> {
  const targets = buildPlacementTargets(loaded)
  const target =
    targets.length === 1
      ? targets[0]!
      : await promptSelectValue(
          context,
          'Where should the new field be inserted?',
          targets.map((candidate) => ({
            value: candidate.id,
            label:
              candidate.scope === 'list-item'
                ? `${candidate.label} (list item scope)`
                : candidate.label
          }))
        ).then((targetId) => targets.find((candidate) => candidate.id === targetId) ?? targets[0]!)

  const order = await promptInsertionOrder(context, target)
  let field = await promptField(context, loaded.kind, schemasDir, target.scope)
  if (loaded.kind !== 'form' && target.id === 'root:fields') {
    const slot = await promptSelectValue(
      context,
      'Field slot',
      [
        { value: 'main', label: 'main' },
        { value: 'sidebar', label: 'sidebar' }
      ],
      'main'
    )
    field = applySlotToField(loaded, target, field, slot)
  }
  const summary = await promptSchemaIntegrationOptions(context, loaded, field, target)

  return {
    field,
    target,
    order,
    summary
  }
}

export async function promptInsertionOrder(
  context: SchemaPromptContext,
  target: AddFieldPlacementTarget
): Promise<{
  before?: string
  after?: string
}> {
  const fields = target.getFields()
  const namedFields = fields
    .map((field) => field.name)
    .filter((name): name is string => typeof name === 'string' && name.length > 0)

  if (namedFields.length === 0) {
    return {}
  }

  const order = await promptSelectValue(context, 'Where in that target?', [
    { value: 'end', label: 'End' },
    { value: 'before', label: 'Before field' },
    { value: 'after', label: 'After field' }
  ] as const)

  if (order === 'end') {
    return {}
  }

  const field = await promptSelectValue(
    context,
    order === 'before' ? 'Insert before which field?' : 'Insert after which field?',
    namedFields.map((name) => ({ value: name, label: name }))
  )

  return order === 'before' ? { before: field } : { after: field }
}

export async function promptField(
  context: SchemaPromptContext,
  kind: GeneratedSchemaKind,
  schemasDir: string,
  scope: 'schema' | 'list-item',
  depth = 0,
  promptOptions: PromptFieldOptions = {}
): Promise<GeneratedSchemaField> {
  const type = promptOptions.typeFirst ? await promptFieldType(context, kind) : null
  const label = await promptText(context, depth === 0 ? 'Field label' : 'Child field label')
  const name = promptOptions.deriveName
    ? deriveAvailableFieldName(label, promptOptions.existingNames)
    : await promptFieldName(context, label)
  const fieldType = type ?? (await promptFieldType(context, kind))

  if (kind !== 'form' && ['group', 'section', 'list'].includes(fieldType)) {
    return promptSchemaContainerField(
      context,
      kind,
      schemasDir,
      scope,
      name,
      label,
      fieldType,
      depth,
      promptOptions
    )
  }

  if (kind !== 'form' && fieldType === 'tabs') {
    return promptTabsField(context, kind, schemasDir, scope, name, label, depth, promptOptions)
  }

  if (kind === 'form' && ['group', 'list'].includes(fieldType)) {
    return promptFormContainerField(
      context,
      kind,
      schemasDir,
      scope,
      name,
      label,
      fieldType,
      depth,
      promptOptions
    )
  }

  const useFormFileOptionsPrompt =
    (await shouldPromptRequired(fieldType)) && kind === 'form' && fieldType === 'file'
  const fileOptions = useFormFileOptionsPrompt
    ? await promptFormFileOptions(context)
    : { required: false, multiple: false }
  const required = useFormFileOptionsPrompt
    ? fileOptions.required
    : (await shouldPromptRequired(fieldType))
      ? await promptConfirm(context, 'Required field?', false)
      : false
  const creatable =
    kind !== 'form' && fieldType === 'select'
      ? await promptConfirm(context, 'Allow editors to create options?', false)
      : false
  const options = await promptFieldOptions(context, kind, fieldType, creatable)
  const multiple = useFormFileOptionsPrompt
    ? fileOptions.multiple
    : await promptMultiple(context, kind, fieldType)
  const relationship = await promptRelationshipTarget(context, kind, fieldType, schemasDir)

  const field = buildLeafField({
    kind,
    type: fieldType,
    name,
    label,
    required,
    multiple,
    creatable,
    options,
    relationship
  })

  applyDerivedFieldDefaults(field, fieldType, promptOptions)
  await applyAdvancedOptions(context, field, kind, fieldType, promptOptions)
  return field
}

export function deriveAvailableFieldName(
  label: string,
  existingNames: Iterable<string> = []
): string {
  const usedNames = new Set(existingNames)
  const rawBase = deriveFieldNameFromLabel(label)
  let base = rawBase

  if (validateGeneratedFieldName(base).length > 0) {
    base = `${base}Field`
  }
  if (validateGeneratedFieldName(base).length > 0) {
    base = 'field'
  }

  let candidate = base
  let index = 2
  while (usedNames.has(candidate) || validateGeneratedFieldName(candidate).length > 0) {
    candidate = `${base}${index}`
    index += 1
  }

  return candidate
}

function getFieldNames(fields: GeneratedSchemaField[]): string[] {
  return fields
    .map((field) => field.name)
    .filter((name): name is string => typeof name === 'string' && name.length > 0)
}

export async function promptFieldName(
  context: SchemaPromptContext,
  label: string
): Promise<string> {
  const defaultName = deriveFieldNameFromLabel(label)
  return promptText(context, 'Field name', {
    defaultValue: defaultName,
    validate(value) {
      const errors = validateGeneratedFieldName(String(value ?? ''))
      return errors[0]
    }
  })
}

const ENTER_PLACEHOLDER_FIELD_TYPES = new Set([
  'string',
  'email',
  'varchar',
  'text',
  'markdown',
  'richtext',
  'number',
  'decimal',
  'textarea',
  'phone',
  'url'
])
const SELECT_PLACEHOLDER_FIELD_TYPES = new Set(['select', 'multiselect'])
const NON_PLACEHOLDER_FIELD_TYPES = new Set([
  'group',
  'section',
  'tabs',
  'separator',
  'list',
  'dynamicFields'
])
const FORM_OPTION_FIELD_TYPE_SET = new Set<string>(FORM_OPTION_FIELD_TYPES)

function derivePlaceholderFromLabel(type: string, label: string): string | undefined {
  const normalizedLabel = label.trim()
  if (!normalizedLabel) {
    return undefined
  }

  const lowerLabel = normalizedLabel.toLowerCase()
  if (SELECT_PLACEHOLDER_FIELD_TYPES.has(type)) {
    return `Select ${lowerLabel}`
  }

  if (ENTER_PLACEHOLDER_FIELD_TYPES.has(type)) {
    return `Enter ${lowerLabel}`
  }

  return undefined
}

function applyDerivedFieldDefaults(
  field: GeneratedSchemaField,
  type: string,
  options: PromptFieldOptions
): void {
  if (!options.derivePlaceholder) {
    return
  }

  const record = field as unknown as Record<string, unknown>
  if (typeof record.placeholder === 'string' && record.placeholder.trim()) {
    return
  }

  const label = typeof record.label === 'string' ? record.label : ''
  const placeholder = derivePlaceholderFromLabel(type, label)
  if (placeholder) {
    record.placeholder = placeholder
  }
}

async function promptFieldType(
  context: SchemaPromptContext,
  kind: GeneratedSchemaKind
): Promise<string> {
  const types = kind === 'form' ? FORM_ADD_FIELD_TYPES : ENTITY_SINGLE_ADD_FIELD_TYPES
  return promptSelectValue(
    context,
    'Field type',
    types.map((type) => ({
      value: type,
      label: type
    }))
  )
}

async function shouldPromptRequired(type: string): Promise<boolean> {
  return !['group', 'section', 'tabs', 'separator'].includes(type)
}

async function promptFormFileOptions(
  context: SchemaPromptContext
): Promise<{ required: boolean; multiple: boolean }> {
  const selected = await p.multiselect({
    message: 'Field options',
    options: [
      { value: 'required' satisfies FieldCheckboxOption, label: 'Required field?' },
      { value: 'multiple' satisfies FieldCheckboxOption, label: 'Allow multiple files?' }
    ],
    required: false
  })
  const values = new Set(cancelIfNeeded(context, selected) as FieldCheckboxOption[])

  return {
    required: values.has('required'),
    multiple: values.has('multiple')
  }
}

async function promptMultiple(
  context: SchemaPromptContext,
  kind: GeneratedSchemaKind,
  type: string
): Promise<boolean> {
  if (kind === 'form') {
    return type === 'file' ? promptConfirm(context, 'Allow multiple files?', false) : false
  }

  return ['select', 'relationship'].includes(type)
    ? promptConfirm(context, 'Allow multiple values?', false)
    : false
}

async function promptFieldOptions(
  context: SchemaPromptContext,
  kind: GeneratedSchemaKind,
  type: string,
  creatable: boolean
): Promise<AddFieldOption[]> {
  const requiresOptions = kind === 'form' ? FORM_OPTION_FIELD_TYPE_SET.has(type) : type === 'select'

  if (!requiresOptions) {
    return []
  }

  if (kind !== 'form' && type === 'select' && creatable) {
    const staticOptions = await promptConfirm(context, 'Add static default options?', false)
    if (!staticOptions) {
      return []
    }
  }

  while (true) {
    const value = await promptText(context, 'Options list', {
      placeholder: 'Sales, Support, General',
      validate: validateInteractiveOptionsList
    })
    try {
      return parseInteractiveOptionsList(value)
    } catch (error) {
      p.log.error(error instanceof Error ? error.message : String(error))
    }
  }
}

function validateInteractiveOptionsList(value: string): string | undefined {
  try {
    parseInteractiveOptionsList(value)
    return undefined
  } catch (error) {
    return error instanceof Error ? error.message : String(error)
  }
}

function parseInteractiveOptionsList(value: string): AddFieldOption[] {
  const labels = value.split(',').map(normalizeOptionLabel).filter(Boolean)

  if (labels.length === 0) {
    throw new Error('Enter option labels separated by commas.')
  }

  const options = labels.map((label) => ({
    value: deriveOptionValueFromLabel(label),
    label
  }))
  const seen = new Set<string>()
  for (const option of options) {
    if (!option.value) {
      throw new Error(`Invalid option label "${option.label}". Use letters or numbers.`)
    }
    if (seen.has(option.value)) {
      throw new Error(`Duplicate option value "${option.value}".`)
    }
    seen.add(option.value)
  }

  return options
}

function normalizeOptionLabel(label: string): string {
  return label
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((word) => {
      if (!word) return word
      return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`
    })
    .join(' ')
}

function deriveOptionValueFromLabel(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-')
}

async function promptRelationshipTarget(
  context: SchemaPromptContext,
  kind: GeneratedSchemaKind,
  type: string,
  schemasDir: string
): Promise<string | undefined> {
  if (kind === 'form' || type !== 'relationship') {
    return undefined
  }

  const schemaNames = listSchemaNames(schemasDir).filter(
    (schemaName) => schemaName !== USER_RELATIONSHIP_TARGET
  )
  const targets = schemaNames
    .map((schemaName) => {
      try {
        const targetKind = readRelationshipTargetKind(schemasDir, schemaName)
        return targetKind === 'form'
          ? null
          : { schemaName, targetKind: targetKind as 'entity' | 'single' | 'user' }
      } catch {
        return null
      }
    })
    .filter((target): target is { schemaName: string; targetKind: 'entity' | 'single' | 'user' } =>
      Boolean(target)
    )

  targets.unshift({
    schemaName: USER_RELATIONSHIP_TARGET,
    targetKind: 'user'
  })

  if (targets.length === 0) {
    fail('No Entity or Single schemas are available as relationship targets.')
  }

  return promptSelectValue(
    context,
    'Relationship target',
    targets.map((target) => ({
      value: target.schemaName,
      label: `${target.schemaName} (${formatKind(target.targetKind)})`
    }))
  )
}

async function promptSchemaContainerField(
  context: SchemaPromptContext,
  kind: GeneratedSchemaKind,
  schemasDir: string,
  scope: 'schema' | 'list-item',
  name: string,
  label: string,
  type: string,
  depth: number,
  options: PromptFieldOptions
): Promise<SchemaField> {
  const field = {
    name,
    type,
    label,
    fields: []
  } as SchemaField

  await applyAdvancedOptions(context, field, kind, type, options)
  await promptChildFields(
    context,
    kind,
    schemasDir,
    type === 'list' ? 'list-item' : scope,
    field.fields!,
    depth,
    options
  )
  return field
}

async function promptFormContainerField(
  context: SchemaPromptContext,
  kind: GeneratedSchemaKind,
  schemasDir: string,
  scope: 'schema' | 'list-item',
  name: string,
  label: string,
  type: string,
  depth: number,
  options: PromptFieldOptions
): Promise<FormField> {
  const field = {
    name,
    type,
    label,
    fields: []
  } as FormField

  await applyAdvancedOptions(context, field, kind, type, options)
  await promptChildFields(
    context,
    kind,
    schemasDir,
    type === 'list' ? 'list-item' : scope,
    field.fields!,
    depth,
    options
  )
  return field
}

export async function promptChildFields(
  context: SchemaPromptContext,
  kind: GeneratedSchemaKind,
  schemasDir: string,
  scope: 'schema' | 'list-item',
  fields: GeneratedSchemaField[],
  depth: number,
  options: PromptFieldOptions = {}
): Promise<void> {
  while (true) {
    const addChild =
      fields.length === 0
        ? await promptConfirm(context, 'Add a child field?', true)
        : await promptConfirm(context, 'Add another child field?', false)

    if (!addChild) {
      if (fields.length === 0) {
        p.log.warn('Containers need at least one child field.')
        continue
      }
      return
    }

    fields.push(
      await promptField(context, kind, schemasDir, scope, depth + 1, {
        ...options,
        existingNames: getFieldNames(fields)
      })
    )
  }
}

async function promptTabsField(
  context: SchemaPromptContext,
  kind: GeneratedSchemaKind,
  schemasDir: string,
  scope: 'schema' | 'list-item',
  name: string,
  label: string,
  depth: number,
  options: PromptFieldOptions
): Promise<SchemaField> {
  const field = {
    name,
    type: 'tabs',
    label,
    tabs: []
  } as SchemaField

  await applyAdvancedOptions(context, field, kind, 'tabs', options)

  while (true) {
    const addTab =
      field.tabs!.length === 0
        ? await promptConfirm(context, 'Add a tab?', true)
        : await promptConfirm(context, 'Add another tab?', false)
    if (!addTab) {
      if (field.tabs!.length === 0) {
        p.log.warn('Tabs need at least one tab.')
        continue
      }
      return field
    }

    const tabLabel = await promptText(context, 'Tab label')
    const tabName = options.deriveName
      ? deriveAvailableFieldName(
          tabLabel,
          field.tabs!.map((tab) => tab.name)
        )
      : await promptFieldName(context, tabLabel)
    const layout = await promptSelectValue(context, 'Tab layout', [
      { value: 'fields', label: 'Single fields array' },
      { value: 'slot', label: 'Main/sidebar slots' }
    ] as const)

    if (layout === 'fields') {
      const tabFields: SchemaField[] = []
      await promptChildFields(context, kind, schemasDir, scope, tabFields, depth + 1, options)
      field.tabs!.push({ name: tabName, label: tabLabel, fields: tabFields })
    } else {
      const mainFields: SchemaField[] = []
      const sidebarFields: SchemaField[] = []
      while (true) {
        const addChild =
          mainFields.length + sidebarFields.length === 0
            ? await promptConfirm(context, 'Add a tab child field?', true)
            : await promptConfirm(context, 'Add another tab child field?', false)
        if (!addChild) {
          if (mainFields.length + sidebarFields.length === 0) {
            p.log.warn('Tabs need at least one child field.')
            continue
          }
          break
        }

        const slot = await promptSelectValue(context, 'Child field slot', [
          { value: 'main', label: 'main' },
          { value: 'sidebar', label: 'sidebar' }
        ] as const)
        const child = (await promptField(context, kind, schemasDir, scope, depth + 1, {
          ...options,
          existingNames: getFieldNames([...mainFields, ...sidebarFields])
        })) as SchemaField
        if (slot === 'sidebar') {
          sidebarFields.push(child)
        } else {
          mainFields.push(child)
        }
      }
      field.tabs!.push({
        name: tabName,
        label: tabLabel,
        slot: {
          ...(mainFields.length > 0 ? { main: { fields: mainFields } } : {}),
          ...(sidebarFields.length > 0 ? { sidebar: { fields: sidebarFields } } : {})
        }
      })
    }
  }
}

async function applyAdvancedOptions(
  context: SchemaPromptContext,
  field: GeneratedSchemaField,
  kind: GeneratedSchemaKind,
  type: string,
  options: PromptFieldOptions
): Promise<void> {
  if (options.promptAdvancedOptions === false) {
    return
  }

  const configure = await promptConfirm(context, 'Configure advanced options?', false)
  if (!configure) {
    return
  }

  const record = field as unknown as Record<string, unknown>
  const omitLabel = await promptConfirm(context, 'Omit the label property?', false)
  if (omitLabel) {
    record.label = undefined
  }

  if (!NON_PLACEHOLDER_FIELD_TYPES.has(type) && !options.derivePlaceholder) {
    const placeholder = await promptOptionalText(context, 'Placeholder')
    if (placeholder) {
      record.placeholder = placeholder
    }
  }

  if (options.promptHint !== false) {
    const hint = await promptOptionalText(context, 'Hint')
    if (hint) {
      record.hint = hint
    }
  }

  if (
    options.promptHeight !== false &&
    ['text', 'textarea', 'markdown', 'richtext'].includes(type)
  ) {
    const height = await promptSelectValue(context, 'Height', [
      { value: 'auto', label: 'auto' },
      { value: 'fill', label: 'fill' }
    ] as const)
    if (height !== 'auto') {
      record.height = height
    }
  }

  if (kind !== 'form' && ['string', 'varchar', 'icon'].includes(type)) {
    const length = await promptOptionalText(context, 'Max length')
    if (length) {
      const parsed = Number(length)
      if (Number.isInteger(parsed) && parsed > 0) {
        record.length = parsed
      } else {
        p.log.warn('Skipped invalid length.')
      }
    }
  }

  if (kind !== 'form' && type === 'decimal') {
    const precision = await promptOptionalText(context, 'Precision')
    const scale = await promptOptionalText(context, 'Scale')
    const parsedPrecision = precision ? Number(precision) : undefined
    const parsedScale = scale ? Number(scale) : undefined
    if (
      parsedPrecision !== undefined &&
      parsedScale !== undefined &&
      Number.isInteger(parsedPrecision) &&
      Number.isInteger(parsedScale)
    ) {
      record.precision = parsedPrecision
      record.scale = parsedScale
    }
  }
}

async function promptSchemaIntegrationOptions(
  context: SchemaPromptContext,
  loaded: LoadedAuthoredGeneratedSchema,
  field: GeneratedSchemaField,
  target: AddFieldPlacementTarget
): Promise<string[]> {
  if (target.scope === 'list-item') {
    return []
  }

  if (loaded.kind === 'form') {
    return promptFormColumnOption(context, loaded.schema as FormSchema, field as FormField)
  }

  if (loaded.kind === 'single') {
    return []
  }

  return promptEntityIntegrationOptions(context, loaded.schema as Schema, field as SchemaField)
}

async function promptEntityIntegrationOptions(
  context: SchemaPromptContext,
  schema: Schema,
  field: SchemaField
): Promise<string[]> {
  const summary: string[] = []
  const columnType = schemaColumnTypeForField(field)
  if (columnType) {
    const addColumn = await promptConfirm(context, 'Add this field to the table columns?', false)
    if (addColumn) {
      schema.columns = schema.columns ?? []
      schema.columns.push({
        accessorKey: field.name,
        header: displayLabel(field),
        type: columnType
      })
      summary.push(`table column: ${field.name}`)
    }
  }

  if (['string', 'email', 'varchar', 'text', 'markdown', 'richtext'].includes(field.type)) {
    const addSearch = await promptConfirm(context, 'Add this field to search?', false)
    if (addSearch) {
      schema.search = schema.search ?? { fields: [] }
      schema.search.fields = Array.from(new Set([...schema.search.fields, field.name]))
      summary.push(`search field: ${field.name}`)
    }
  }

  if (field.type === 'select' || field.type === 'boolean') {
    const addFilter = await promptConfirm(context, 'Add this field as a list filter?', false)
    if (addFilter) {
      schema.filters = schema.filters ?? []
      schema.filters.push({
        field: field.name,
        label: displayLabel(field),
        type: field.type === 'boolean' ? 'boolean' : 'select',
        ...(field.type === 'select' && field.options?.length
          ? {
              source: 'static' as const,
              options: field.options
            }
          : field.type === 'select'
            ? { source: 'distinct' as const }
            : {})
      })
      summary.push(`filter: ${field.name}`)
    }
  }

  return summary
}

async function promptFormColumnOption(
  context: SchemaPromptContext,
  schema: FormSchema,
  field: FormField
): Promise<string[]> {
  const columnType = formColumnTypeForField(field)
  if (!field.name || !columnType) {
    return []
  }

  const addColumn = await promptConfirm(
    context,
    'Add this field to submission admin columns?',
    false
  )
  if (!addColumn) {
    return []
  }

  schema.columns = schema.columns ?? []
  const column: FormColumn = {
    accessorKey: field.name,
    header: displayLabel(field),
    type: columnType
  }
  schema.columns.push(column)
  return [`submission column: ${field.name}`]
}

function schemaColumnTypeForField(field: SchemaField): SchemaColumn['type'] | undefined {
  switch (field.type) {
    case 'image':
    case 'media':
    case 'video':
      return 'image'
    case 'date':
    case 'timestamp':
      return 'date'
    case 'number':
    case 'decimal':
      return 'number'
    case 'boolean':
      return 'boolean'
    case 'select':
      return 'badge'
    case 'email':
      return 'email'
    case 'string':
    case 'varchar':
    case 'text':
    case 'markdown':
    case 'richtext':
    case 'time':
    case 'icon':
    case 'relationship':
      return 'text'
    default:
      return undefined
  }
}

export function formColumnTypeForField(field: FormField): FormColumn['type'] | undefined {
  switch (field.type) {
    case 'email':
      return 'email'
    case 'date':
      return 'date'
    case 'number':
      return 'number'
    case 'select':
    case 'radio':
    case 'checkbox':
    case 'multiselect':
      return 'badge'
    case 'text':
    case 'textarea':
    case 'phone':
    case 'url':
    case 'timezone':
      return 'text'
    default:
      return undefined
  }
}

export function displayLabel(field: GeneratedSchemaField): string {
  return typeof field.label === 'string' && field.label.trim()
    ? field.label
    : typeof field.name === 'string'
      ? field.name
      : 'Field'
}
